import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Drawer,
  Group,
  Paper,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  UnstyledButton,
  useComputedColorScheme,
} from '@mantine/core';
import { useTranslation } from '@refinedev/core';
import {
  IconAlertTriangle,
  IconCheck,
  IconClock,
  IconEye,
  IconInfoCircle,
  IconSearch,
  IconX,
} from '@tabler/icons-react';
import { useState } from 'react';
import {
  ALL_CLAIM_TAB,
  useClaimTabFilterStore,
} from 'store/ClaimTabFilterStore';
import { useClaimPaginationStore } from 'store/ClaimPaginationStore';
import {
  getStatusChipPalette,
  type StatusChipFamilyKey,
} from 'utils/badgeColors';
import {
  providerClaimLegendCopy,
  providerClaimLegendStages,
  type LegendGroupDefinition,
  type LegendItemDefinition,
  type LegendStageDefinition,
} from './providerClaimLegendData';
import { providerClaimLegendStyles as styles } from './ProviderClaimsStyles';

const TRANSLATION_PREFIX = 'pages:claims.providerStatusLegend';
const groupIcons = { eye: IconEye, clock: IconClock };

const LegendStatusChip = ({
  label,
  family,
}: {
  label: string;
  family: StatusChipFamilyKey;
}) => {
  const colorScheme = useComputedColorScheme('dark');
  return (
    <Badge
      radius="xl"
      variant="light"
      tt="none"
      style={styles.getStatusChip(getStatusChipPalette(family, colorScheme))}
    >
      {label}
    </Badge>
  );
};

const ActionNeededIcon = () => (
  <ThemeIcon
    component="span"
    size={16}
    radius="xl"
    color="red"
    variant="filled"
    style={styles.inlineControl}
    aria-hidden
  >
    <IconAlertTriangle size={12} color="white" />
  </ThemeIcon>
);

const LegendItem = ({
  item,
  grouped = false,
}: {
  item: LegendItemDefinition;
  grouped?: boolean;
}) => {
  const { translate } = useTranslation();
  return (
    <Box style={styles.getItem(grouped)}>
      <LegendStatusChip
        family={item.family}
        label={translate(
          `${TRANSLATION_PREFIX}.items.${item.key}.label`,
          item.label
        )}
      />
      <Text c="dimmed" size="sm" lh={1.45}>
        {translate(
          `${TRANSLATION_PREFIX}.items.${item.key}.description`,
          item.description
        )}
      </Text>
    </Box>
  );
};

const LegendGroup = ({ group }: { group: LegendGroupDefinition }) => {
  const { translate } = useTranslation();
  const prefix = `${TRANSLATION_PREFIX}.groups.${group.key}`;
  const Icon = groupIcons[group.icon];
  return (
    <Paper
      component="section"
      aria-label={translate(`${prefix}.title`, group.title)}
      style={styles.groupCard}
    >
      <Group gap="xs">
        <Icon size={15} />
        <Text fw={700} size="sm">
          {translate(`${prefix}.title`, group.title)}
        </Text>
        {group.chip && (
          <Badge
            size="xs"
            radius="xl"
            color="gray"
            variant="light"
            tt="none"
            leftSection={<IconCheck size={11} />}
          >
            {translate(`${prefix}.chip`, group.chip)}
          </Badge>
        )}
      </Group>
      <Text c="dimmed" size="sm" lh={1.45}>
        {translate(`${prefix}.note`, group.note)}
      </Text>
      {group.items.map((item) => (
        <LegendItem key={item.key} item={item} grouped />
      ))}
      <Paper style={styles.groupCallout}>
        <IconInfoCircle size={16} style={styles.infoIcon} />
        <Text size="sm" lh={1.5}>
          {translate(`${prefix}.noAction`, group.noAction)}{' '}
          {group.respond && <ActionNeededIcon />}
        </Text>
      </Paper>
    </Paper>
  );
};

const LegendStage = ({ stage }: { stage: LegendStageDefinition }) => {
  const { translate } = useTranslation();
  const colorScheme = useComputedColorScheme('dark');
  const prefix = `${TRANSLATION_PREFIX}.stages.${stage.key}`;
  const palette = getStatusChipPalette(stage.family, colorScheme);
  return (
    <Paper
      component="section"
      aria-label={translate(`${prefix}.title`, stage.title)}
      style={styles.card}
    >
      <Group gap="sm" wrap="nowrap">
        <Text
          component="span"
          c={palette.textColor}
          fw={700}
          style={styles.getStageNumber(palette)}
        >
          {stage.number}
        </Text>
        <Text fw={700} size="sm">
          {translate(`${prefix}.title`, stage.title)}
        </Text>
      </Group>
      <Text c="dimmed" size="sm" lh={1.4}>
        {translate(`${prefix}.note`, stage.note)}
      </Text>
      {stage.items.map((item) => (
        <LegendItem key={item.key} item={item} />
      ))}
      {stage.group && <LegendGroup group={stage.group} />}
    </Paper>
  );
};

export const ProviderClaimStatusLegend = () => {
  const { translate } = useTranslation();
  const [opened, setOpened] = useState(false);
  const [query, setQuery] = useState('');
  const text = (key: keyof typeof providerClaimLegendCopy): string =>
    translate(`${TRANSLATION_PREFIX}.${key}`, providerClaimLegendCopy[key]);
  const search = query.trim().toLocaleLowerCase();
  const matchesText = (value: string): boolean =>
    value.toLocaleLowerCase().includes(search);
  const matches = (item: LegendItemDefinition): boolean => {
    const prefix = `${TRANSLATION_PREFIX}.items.${item.key}`;
    return matchesText(
      [
        translate(`${prefix}.label`, item.label),
        translate(`${prefix}.description`, item.description),
      ].join(' ')
    );
  };
  const filterGroup = (
    group: LegendGroupDefinition | undefined
  ): LegendGroupDefinition | undefined => {
    if (!group) return undefined;
    const title = translate(
      `${TRANSLATION_PREFIX}.groups.${group.key}.title`,
      group.title
    );
    if (matchesText(title)) return group;
    const items = group.items.filter(matches);
    if (items.length === 0) return undefined;
    return { ...group, items };
  };
  const stages = providerClaimLegendStages
    .map((stage) => {
      const title = translate(
        `${TRANSLATION_PREFIX}.stages.${stage.key}.title`,
        stage.title
      );
      if (matchesText(title)) return stage;
      return {
        ...stage,
        items: stage.items.filter(matches),
        group: filterGroup(stage.group),
      };
    })
    .filter((stage) => stage.items.length > 0 || stage.group);

  const showNeedsYou = () => {
    const tabs = useClaimTabFilterStore.getState();
    const pagination = useClaimPaginationStore.getState();
    tabs.setActiveTab(ALL_CLAIM_TAB);
    tabs.clearSubStatuses();
    if (!tabs.providerNeedsAction) tabs.toggleProviderNeedsAction();
    pagination.setColumnFilters(
      pagination.pagination.columnFilters.filter(
        (filter) => filter.id !== 'payorID'
      )
    );
    pagination.setPageIndex(0);
    setOpened(false);
  };

  return (
    <>
      <Stack gap={2} style={styles.triggerContainer}>
        <Text title={text('workflowTooltip')} style={styles.workflowLabel}>
          <IconEye size={13} />
          {text('workflowLabel')}
        </Text>
        <UnstyledButton
          onClick={() => setOpened(true)}
          aria-label={text('open')}
          style={styles.trigger}
        >
          <Text component="span" style={styles.questionIcon}>
            ?
          </Text>
          {text('open')}
        </UnstyledButton>
      </Stack>
      <Drawer
        opened={opened}
        onClose={() => setOpened(false)}
        position="right"
        size={460}
        title={text('title')}
        closeButtonProps={{ 'aria-label': text('close') }}
        overlayProps={{ backgroundOpacity: 0.35 }}
        styles={styles.drawer}
      >
        <Box style={styles.searchBar}>
          <TextInput
            value={query}
            onChange={(event) => setQuery(event.currentTarget.value)}
            placeholder={text('search')}
            aria-label={text('search')}
            leftSection={<IconSearch size={16} />}
            rightSection={
              query && (
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  aria-label={text('clearSearch')}
                  onClick={() => setQuery('')}
                >
                  <IconX size={15} />
                </ActionIcon>
              )
            }
          />
        </Box>
        <Stack gap="md" style={styles.content}>
          {!search && (
            <>
              <Paper style={styles.visibilityCallout}>
                <IconInfoCircle size={17} style={styles.infoIcon} />
                <Text size="sm" lh={1.5}>
                  <Text component="span" inherit fw={700}>
                    {text('visibilityTitle')}
                  </Text>{' '}
                  <Text component="span" inherit c="dimmed">
                    {text('visibilityBeforeRespond')} <ActionNeededIcon />{' '}
                    {text('visibilityBeforeFilter')}{' '}
                    <Button
                      variant="light"
                      color="red"
                      size="compact-xs"
                      leftSection={<IconAlertTriangle size={13} />}
                      onClick={showNeedsYou}
                      style={{
                        ...styles.inlineControl,
                        color: 'var(--mantine-color-red-light-color)',
                      }}
                    >
                      {text('needsYou')}
                    </Button>{' '}
                    {text('visibilityAfterFilter')}
                  </Text>
                </Text>
              </Paper>
              <Stack gap={6}>
                <Text c="dimmed" size="sm" lh={1.45}>
                  {text('overviewClaim')}
                </Text>
                <Text c="dimmed" size="sm" lh={1.45}>
                  {text('overviewTariffs')}
                </Text>
              </Stack>
            </>
          )}
          {stages.length === 0 && (
            <Text role="status" c="dimmed" ta="center" py="xl">
              {translate(
                `${TRANSLATION_PREFIX}.noMatch`,
                { query },
                `No tariff status matches “${query}”.`
              )}
            </Text>
          )}
          {stages.map((stage) => (
            <LegendStage key={stage.key} stage={stage} />
          ))}
          {!search && (
            <>
              <Paper style={styles.card}>
                <Group gap="sm">
                  <LegendStatusChip family="gray" label={text('mixedLabel')} />
                  <Badge size="xs" radius="xl" color="gray" variant="light">
                    3
                  </Badge>
                </Group>
                <Text c="dimmed" size="sm" lh={1.45}>
                  {text('mixedDescription')}
                </Text>
              </Paper>
              <Text c="dimmed" size="sm" lh={1.45}>
                {text('footer')}
              </Text>
            </>
          )}
        </Stack>
      </Drawer>
    </>
  );
};
